import api from 'api'

export type PaymentProvider =
  | 'everee'
  | 'highnote'
  | 'stripe'
  | 'marqeta'
  | 'bbsi'

export type Environment = 'production' | 'sandbox'

export type MarqetaProfileStatus =
  | 'unverified'
  | 'limited'
  | 'active'
  | 'suspended'
  | 'closed'

export type MarqetaKYCStatus = 'none' | 'success' | 'failure'

export type MarqetaCardStatus =
  | 'active'
  | 'suspended'
  | 'terminated'
  | 'unsupported'
  | 'unactivated'
  | 'limited'

export type DirectDepositStatus = 'none' | 'submitted' | 'failed'

export type FederalTaxWithholdingStatus = 'none' | 'submitted' | 'processed'

export type TinVerificationStatus = 'none' | 'passed' | 'failed'

// External Bank Account (shared by both Stripe and Marqeta)
export interface ExternalBankAccount {
  accountType: string | null
  displayName: string | null
  last4: string | null
  status: MarqetaCardStatus | null
  // Stripe-specific fields
  stripeIsDefault: boolean | null
  stripeCanInstantPay: boolean | null
}

// Stripe types
export interface StripeProfileResponse {
  accountId: string | null
  externalBankAccounts: ExternalBankAccount[]
}

// Marqeta types

export interface MarqetaProfileResponse {
  accountId: string | null
  environment: Environment | null
  profileStatus: MarqetaProfileStatus | null
  kycStatus: MarqetaKYCStatus | null
  cards: ExternalBankAccount[]
  externalBankAccounts: ExternalBankAccount[]
}

// Everee types
export interface EvereeProfileResponse {
  version: 'v2' | 'v3'
  accountId: string
  federalTaxWithholdingStatus: FederalTaxWithholdingStatus | null
  directDepositStatus: DirectDepositStatus | null
  tinVerificationStatus: TinVerificationStatus | null
  evereeOnboardingSubmittedAt: string | null
  externalBankAccounts: ExternalBankAccount[]
}

// Main response type
export type W2PayrollFrequency = 'daily' | 'weekly'
export interface AdminUserPaymentProfileResponse {
  current1099PaymentProvider: PaymentProvider
  w2PayrollFrequency: W2PayrollFrequency
  w2GarnishmentsNeeded: boolean
  isTransitioningFromStripeToHighnote?: boolean
  stripeProfile: StripeProfileResponse | null
  marqetaProfile: MarqetaProfileResponse | null
  evereeProfile: EvereeProfileResponse | null
}

// API function
export type GetUserPaymentProfileOptions = {
  userId: number
}

export const getUserPaymentProfile = async ({
  userId,
}: GetUserPaymentProfileOptions): Promise<AdminUserPaymentProfileResponse> => {
  const response = await api.get(`/admin/user_payments/${userId}/profile`)
  return response.data
}

export type UpdateUserPaymentProfileOptions = {
  userId: number
  w2PayrollFrequency: W2PayrollFrequency | null
  w2GarnishmentsNeeded: boolean | null
}

export const updateUserPaymentProfile = async ({
  userId,
  w2PayrollFrequency,
  w2GarnishmentsNeeded,
}: UpdateUserPaymentProfileOptions): Promise<void> => {
  await api.patch(`/admin/user_payments/${userId}/profile`, {
    w2PayrollFrequency,
    w2GarnishmentsNeeded,
  })
}
